import React, { useState } from "react";
import { useQuery, useMutation } from '@apollo/client';
import { ColumnsType } from "antd/es/table";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import {Modal, Radio} from "antd";
import { GET_USERS, UPDATE_USER, DELETE_USER, CREATE_USER, User } from "../../../utils/api/user";
import { UserRole, UserStatus } from "../../../__generated__/graphql";
import Table from "../../../components/lists/Table/Table";
import { useCurrentUser } from "../../../utils/CurrentUserContext";
import FormModal, { validateEmail } from "../../../components/modals/FormModal";
import SingleLineField from '../../../components/forms/SingleLineField';
import DropdownField from "../../../components/forms/DropdownField";
import ActionButton, { ActionHandler } from "../../../components/lists/Table/shared/ActionButton";
import Badge, { BadgeColor } from "../../../components/badges/Badge";
import SettingsCard from "./SettingsCard";
import {useParams} from 'react-router-dom';
import {useQueryVariables } from "../../../utils/hooks";
import {Controller, useFormContext} from 'react-hook-form';
import FormItem from "../../../components/forms/utils/FormItem";



const roles = {
  [UserRole.RegularUser]: { text: 'Regular user', color: BadgeColor.BLUE },
  [UserRole.AccountAdmin]: { text: 'Account admin', color: BadgeColor.GREEN },
  [UserRole.SuperAdmin]: { text: 'Super admin', color: BadgeColor.GREEN },
  [UserRole.Guest]: { text: 'Guest user', color: BadgeColor.PINK },
}

const statuses = {
  [UserStatus.Invited]: { text: UserStatus.Invited, color: BadgeColor.BLUE },
  [UserStatus.Active]: { text: UserStatus.Active, color: BadgeColor.GREEN },
  [UserStatus.Deleted ]: { text: UserStatus.Deleted, color: BadgeColor.PINK },
}

const getColumns = (actions?: {onEdit: ActionHandler<User>, onDelete: ActionHandler<User>}): ColumnsType<User> => [
  {
    key: "name",
    title: "Name",
    dataIndex: 'fullname',
  },
  {
    key: "title",
    title: "Title",
    dataIndex: 'title',
  },
  {
    key: "email",
    title: "Email",
    dataIndex: 'email',
  },
  {
    key: "role",
    title: "Role",
    dataIndex: 'role',
    render: (role: UserRole) => {
      return <Badge text={roles[role].text} color={roles[role].color} />
    }
  },
  {
    key: "status",
    title: "Status",
    dataIndex: 'status',
    render: (status: UserStatus) => {
      return <Badge text={statuses[status].text} color={statuses[status].color} />
    }
  },
  ...actions ? [
    {
      key: "actions",
      title: "",
      render: (_, row) => <div className="px-3">
        <ActionButton tooltip="Edit user" row={row} onClick={actions.onEdit} Icon={PencilIcon} />
        <ActionButton tooltip="Delete user" row={row} onClick={actions.onDelete} Icon={TrashIcon} danger />
      </div>,
    }
  ] as ColumnsType<User> : []

]

const AllowPasswordLogin = () => {
  const form = useFormContext();

  return <FormItem top>
    <Controller
      name='allowPasswordLogin'
      control={form.control}
      render={({ field: { onChange, value } }) => (
        <Radio.Group onChange={onChange} value={value}>
          <Radio value={false}>Force IdP to login</Radio>
          <Radio value={true}>Allow to use password</Radio>
        </Radio.Group>
      )}
    />
  </FormItem>
}

const CreateUserForm = () => {
  const {variables, setPagination, setFilters} = useQueryVariables();
  const { page } = useParams();

  const {user: {companyId}, isAdmin} = useCurrentUser()
  
  const { loading, data } = useQuery(GET_USERS, {
    variables
  })

  const [createUser] = useMutation(CREATE_USER, {
    refetchQueries: ['UserMany']
  })

  const [updateUser] = useMutation(UPDATE_USER)
  const [deleteUser] = useMutation(DELETE_USER, {
    refetchQueries: ['UserMany']
  })

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [user, setUser] = useState<Partial<User>>();

  const handleOk = async (values: User) => {
    if (user) {
      const { email, _id, ...record } = values
      updateUser({ variables: { id: user._id, record } })
    } else {
      validateEmail(values.email, 'email')
      try {
        await createUser({ variables: { record: { ...values, companyId } } })
      } catch (error: any) {
        // TODO: Consider improving this check
        if (error.message.startsWith('E11000 duplicate key error collection: development.users index: email_1')) {
          // eslint-disable-next-line no-throw-literal
          throw { field: 'email', formError: { type: 'duplicated_email', message: 'A user already exists with this email' } }
        }
      }
    }
    setModalVisible(false);
  };

  const handleEditUser = (user: User) => {
    const { __typename, status, fullname, company, ...newUser } = user

    setUser(newUser)
    setModalVisible(true)
  }

  const handleDeleteUser = (user: User) => {
    Modal.confirm({
      title: 'Are you sure?',
      content: <p>Are you sure you want to delete <strong>{user.fullname} ({user.email})</strong>?</p>,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteUser({ variables: { id: user._id } })
    })
  }

  const tableProps = isAdmin
    ? {
      actionButtonText: 'Add User',
      onActionButtonClick: () => setModalVisible(true),
      columns: getColumns({ onEdit: handleEditUser, onDelete: handleDeleteUser })
    }
    : {
      columns: getColumns()
    }

  return (
    <SettingsCard
      title="Users"
      description="A list of all the users in your company account including their name, title, email and role."
    >
        <Table
          className="mt-8"
          data={data?.users.items || []}
          pagination={{
            total: data ? data.users.meta.totalItems : 0,
            pageSize: variables.perPage,
            current: variables.page
          }}
          onChange={(pagination, filters) =>{
            setPagination(pagination)
            setFilters(filters)
          }}
          loading={loading}
          {...tableProps}
        />
      <FormModal
        className="w-[500px]"
        open={modalVisible}
        title={`${user ? 'Edit' : 'Add'} User`}
        okText={user ? 'Save' : 'Add'}
        defaultValues={user as User}
        onCancel={() => {
          setModalVisible(false)
          setUser(undefined)
        }}
        onOk={handleOk}
      >
        <SingleLineField
          top
          label="First Name"
          htmlfor="firstname"
          required
        />
        <SingleLineField
          label="Last Name"
          htmlfor="lastname"
          required
        />
        <SingleLineField
          label="Title"
          htmlfor="title"
          required
        />
        <SingleLineField
          label="Email"
          htmlfor="email"
          disabled={!!user}
          required
        />
        <AllowPasswordLogin />
        <DropdownField
          label="Role"
          htmlfor="role"
          options={[
            { value: UserRole.RegularUser, label: roles[UserRole.RegularUser].text },
            { value: UserRole.AccountAdmin, label: roles[UserRole.AccountAdmin].text },
          ]}
          required
        />
      </FormModal>

    </SettingsCard>
  );
};

export default CreateUserForm;
